export const packageTitle = (option) => option.displayTitle?.trim() || option.label || option.name || option.serviceOptionId;
// Use the same regional convention for dates, rates and money. The portal UI is English;
// the separately supported Dutch provider view keeps Dutch formatting.
export function faqtoringLocale(language) {
    return language.toLowerCase().startsWith('nl') ? 'nl-NL' : 'en-GB';
}
// Display the provider's integer amounts; never calculate a replacement quote.
export function formatMoney(minor, language) {
    if (!Number.isSafeInteger(minor))
        return '—';
    return new Intl.NumberFormat(faqtoringLocale(language), { style: 'currency', currency: 'EUR' }).format(minor / 100);
}
export function quoteMatches(quote, invoiceId, now) {
    return quote.response.sourceInvoiceId === invoiceId && quote.termsDigest === quote.response.termsDigest &&
        !!quote.termsDigest && Number.isFinite(Date.parse(quote.expiresAt)) && Date.parse(quote.expiresAt) > now;
}
export function hasReadableTerms(quote) {
    const text = quote.response.commercialTerms.termsText;
    return typeof text === 'string' && text.trim().length > 0 && text.length <= 32000;
}
export function safeHttpsUrl(value) {
    if (!value)
        return undefined;
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && !url.username && !url.password ? url.href : undefined;
    }
    catch {
        return undefined;
    }
}
// Automatic payout route: the freelancer's standing choice between QPortal's own ordinary
// payout and a live Faqtoring package. Amounts only ever come from the provider quote.
export const ORDINARY_PAYOUT = 'ordinary';
export const PAYOUT_BLOCKERS = ['SERVICE_DISABLED', 'ACCOUNT_ONBOARDING_REQUIRED', 'DEPARTMENT_UNKNOWN', 'DEBTOR_REVIEW_REQUIRED',
    'DEPARTMENT_SERVICE_DISABLED', 'PROVIDER_UNAVAILABLE', 'NO_PACKAGES_AVAILABLE'];
/** The terms of the current version were accepted earlier, or no separate version is configured
 *  (the Faqtoring terms are then part of QPortal's general terms). */
export const payoutTermsAccepted = (preference) => !preference.terms.version ||
    !!preference.termsAcceptedAt && !!preference.termsVersion && preference.termsVersion === preference.terms.version && !preference.termsReacceptRequired;
/** The usable standing choice: a vanished package or outdated terms fall back to ordinary. */
export function defaultPayoutChoice(preference) {
    if (preference.blocker || preference.route !== 'faqtoring' || preference.packageUnavailable || !payoutTermsAccepted(preference))
        return ORDINARY_PAYOUT;
    return preference.options.some(x => x.serviceOptionId === preference.serviceOptionId) ? preference.serviceOptionId : ORDINARY_PAYOUT;
}
export const packageName = (option) => option.name?.trim() || option.displayTitle?.trim() || option.label?.trim() || option.serviceOptionId;
/** Faqtoring puts the speed in the package label (e.g. "1 dag · +0,40%"); never guess when absent. */
export function packageDays(option) {
    if (Number.isSafeInteger(option.paymentDays) && option.paymentDays > 0)
        return option.paymentDays;
    const match = /(\d+)\s*(?:dag|dagen|day|days)\b/i.exec(option.label || '') || /(\d+)\s*(?:dag|dagen|day|days)\b/i.exec(option.name || '');
    const days = match ? Number(match[1]) : NaN;
    return Number.isSafeInteger(days) && days > 0 ? days : undefined;
}
/** Display a provider calendar date (yyyy-MM-dd) without a time-zone shift: dd-mm-jjjj in Dutch. */
export function formatPayoutDate(value, language) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match)
        return '—';
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return new Intl.DateTimeFormat(faqtoringLocale(language), { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(date);
}
/** True when this invoice uses (or used, before a decline) the automatic Faqtoring route. */
export const isAutomaticPayout = (payout, request) => !!payout && (payout.route === 'faqtoring' || (!!payout.serviceOptionId && !!request));
export function automaticPayoutStage(payout, request) {
    if (request?.payoutStatus === 'confirmed_paid')
        return 'paid';
    if (request && (['declined', 'canceled'].includes(request.applicationStatus) || request.paymentRouteState === 'ordinary_restored' || payout.route !== 'faqtoring'))
        return 'declined';
    if (payout.expectedPayoutDate || request?.applicationStatus === 'accepted')
        return 'approved';
    if (payout.awaitingProvider || request)
        return 'atProvider';
    return 'submitted';
}
/** Total provider fee for this department as published by Faqtoring, e.g. "1,10%"; undefined when not published. */
export function packageTotalFee(option, language) {
    const bps = option.totalFeeRateBps;
    if (!Number.isSafeInteger(bps) || bps < 0)
        return undefined;
    return new Intl.NumberFormat(faqtoringLocale(language), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(bps / 100) + '%';
}
